'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSession } from 'next-auth/react';
import { adviceService } from '@/lib/services';
import { useScopedI18n } from '@/locales/client';
import { ArrowRight, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { logger } from '@/lib/utils/logger';

interface FollowUpQuestion {
  id: string;
  adviceItemId: string;
  question: string;
  category: string;
  order: number;
  generationMethod: string;
  relevanceScore: number;
  isClicked: boolean;
  clickedAt: string | null;
}

interface FollowUpQuestionsProps {
  adviceItemId: string;
  onQuestionClick: (question: string) => void;
  isVisible?: boolean;
}

// Delay before first fetch (give backend time to generate follow-ups)
const INITIAL_FETCH_DELAY = 3000; // 3 seconds
// Retry delay if no follow-ups found
const RETRY_DELAY = 3000; // 3 seconds
// Maximum number of retries
const MAX_RETRIES = 6;

export const FollowUpQuestions: React.FC<FollowUpQuestionsProps> = ({
  adviceItemId,
  onQuestionClick,
  isVisible = true,
}) => {
  const [followUps, setFollowUps] = useState<FollowUpQuestion[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [clickedId, setClickedId] = useState<string | null>(null);
  const { data: session } = useSession();
  const t = useScopedI18n('chat');

  // Track retries and prevent duplicate fetches
  const retryCountRef = useRef(0);
  const isFetchingRef = useRef(false);
  const lastFetchedIdRef = useRef<string | null>(null);

  // Fetch follow-up questions with retry logic
  const fetchFollowUps = useCallback(async (isRetry = false) => {
    // Skip if no session, no adviceItemId, or if it's a temporary ID
    if (!session || !adviceItemId) {
      setIsLoading(false);
      return;
    }

    // Skip temporary IDs
    if (adviceItemId.includes('temp')) {
      setIsLoading(false);
      return;
    }

    // Prevent duplicate fetches
    if (isFetchingRef.current) {
      return;
    }

    isFetchingRef.current = true;

    try {
      const status = await adviceService.getAdviceItemStatus(adviceItemId, session); // check if not BLOCKED
      logger.debug('status', status);
      const data = await adviceService.getFollowUps(adviceItemId, session);

      if (data && data.length > 0) {
        setFollowUps(data);
        retryCountRef.current = 0; // Reset retry count on success
        lastFetchedIdRef.current = adviceItemId;
      } else if (retryCountRef.current < MAX_RETRIES) {
        // No follow-ups found, schedule a retry
        retryCountRef.current++;
        logger.debug(`[FollowUpQuestions] No follow-ups found, retry ${retryCountRef.current}/${MAX_RETRIES} in ${RETRY_DELAY}ms`);

        setTimeout(() => {
          isFetchingRef.current = false;
          fetchFollowUps(true);
        }, RETRY_DELAY);
        return; // Don't set isLoading to false yet
      }
    } catch (error) {
      console.error('[FollowUpQuestions] Failed to fetch follow-ups:', error);
      setFollowUps([]);
    } finally {
      isFetchingRef.current = false;
      setIsLoading(false);
    }
  }, [adviceItemId, session]);

  // Fetch follow-up questions when adviceItemId changes
  useEffect(() => {
    // Reset state when adviceItemId changes
    if (adviceItemId !== lastFetchedIdRef.current) {
      setFollowUps([]);
      setClickedId(null);
      setIsLoading(true);
      retryCountRef.current = 0;
      isFetchingRef.current = false;
    }

    // Skip if no session or temporary ID
    if (!session || !adviceItemId || adviceItemId.includes('temp')) {
      setIsLoading(false);
      return;
    }

    // Add initial delay to give backend time to generate follow-ups
    const timeoutId = setTimeout(() => {
      fetchFollowUps();
    }, INITIAL_FETCH_DELAY);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [adviceItemId, session, fetchFollowUps]);

  // Handle follow-up click
  const handleClick = useCallback(
    async (followUp: FollowUpQuestion) => {
      if (!session || clickedId) return;

      setClickedId(followUp.id);

      try {
        // Mark as used in the backend
        await adviceService.useFollowUp(followUp.id, session);

        // Trigger the question submission
        onQuestionClick(followUp.question);
      } catch (error) {
        console.error('Failed to use follow-up:', error);
        setClickedId(null);
      }
    },
    [session, clickedId, onQuestionClick]
  );

  // Don't render if not visible
  if (!isVisible) {
    return null;
  }

  // Don't render anything while loading - no skeleton
  if (isLoading || followUps.length === 0) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      className="mt-4 w-full"
    >
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="h-4 w-4 text-purple-500" />
        <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
          {t('followUp.title') || 'Suggested follow-up questions'}
        </span>
      </div>

      <div className="flex flex-col gap-2">
        <AnimatePresence>
          {followUps.map((followUp, index) => (
            <motion.button
              key={followUp.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ delay: index * 0.1, duration: 0.2 }}
              onClick={() => handleClick(followUp)}
              disabled={!!clickedId}
              className={cn(
                'group flex items-center justify-between w-full px-4 py-3 text-left',
                'rounded-lg border border-gray-200 dark:border-gray-700',
                'bg-white dark:bg-gray-800/50',
                'hover:border-purple-300 dark:hover:border-purple-600',
                'hover:bg-purple-50 dark:hover:bg-purple-900/20',
                'transition-all duration-200 ease-in-out',
                'focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2',
                clickedId === followUp.id && 'bg-purple-100 dark:bg-purple-900/40 border-purple-400',
                clickedId && clickedId !== followUp.id && 'opacity-50 cursor-not-allowed'
              )}
            >
              <span className="text-sm text-gray-700 dark:text-gray-300 pr-2">
                {followUp.question}
              </span>
              <ArrowRight
                className={cn(
                  'h-4 w-4 flex-shrink-0 text-gray-400',
                  'group-hover:text-purple-500 group-hover:translate-x-1',
                  'transition-all duration-200',
                  clickedId === followUp.id && 'animate-pulse text-purple-500'
                )}
              />
            </motion.button>
          ))}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

FollowUpQuestions.displayName = 'FollowUpQuestions';
